// src/components/newsletter/Newsletter.jsx
import React, { useState } from 'react';
import './newsletter.css';

const BLOG_URL = 'https://blog.spinosoftbits.com';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const Newsletter = () => {
    const [email, setEmail] = useState('');
    const [subscribed, setSubscribed] = useState(false);
    const [selectedTopics, setSelectedTopics] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Topics that reflect what I actually build and write about
    const topics = [
        { id: 'webdev',    name: 'Web Development',    icon: 'bx-code-alt' },
        { id: 'backend',   name: 'Backend & APIs',     icon: 'bx-server' },
        { id: 'react',     name: 'React & JavaScript', icon: 'bxl-react' },
        { id: 'cloud',     name: 'Cloud & DevOps',     icon: 'bx-cloud' },
        { id: 'ecommerce', name: 'E-Commerce',         icon: 'bx-cart' },
        { id: 'seo',       name: 'SEO & Performance',  icon: 'bx-line-chart' }
    ];

    const benefits = [
        { icon: 'bx-code-block', title: 'Real build notes', text: 'How features and fixes actually get shipped — not theory.' },
        { icon: 'bx-bulb',       title: 'Tips & tutorials', text: 'Backend, React, and deployment lessons you can use.' },
        { icon: 'bx-rocket',     title: 'New posts first',  text: 'Fresh writing the moment it goes live.' }
    ];

    const handleSubscribe = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        const selectedTopicNames = topics
            .filter(t => selectedTopics.includes(t.id))
            .map(t => t.name);
        const topicsToSend = selectedTopicNames.length > 0 ? selectedTopicNames : ['Web Development'];

        try {
            const response = await fetch(`${API_URL}/api/newsletter/subscribe`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, topics: topicsToSend })
            });
            const data = await response.json();

            if (response.ok && data.success) {
                setSubscribed(true);
                setTimeout(() => {
                    setSubscribed(false);
                    setEmail('');
                    setSelectedTopics([]);
                }, 6000);
            } else {
                throw new Error(data.error || 'Failed to subscribe');
            }
        } catch (err) {
            console.error('Subscription error:', err);
            setError('Something went wrong. Please try again, or email hello@spinosoftbits.com.');
        } finally {
            setLoading(false);
        }
    };

    const toggleTopic = (topicId) => {
        setSelectedTopics(prev =>
            prev.includes(topicId) ? prev.filter(id => id !== topicId) : [...prev, topicId]
        );
    };

    return (
        <section className="newsletter section" id="newsletter">
            <div className="newsletter__container container">
                <div className="newsletter__grid">
                    {/* Left — the pitch */}
                    <div className="newsletter__intro">
                        <span className="newsletter__eyebrow">
                            <i className="bx bx-envelope-open"></i> Newsletter
                        </span>
                        <h2 className="newsletter__title">Build notes, straight to your inbox</h2>
                        <p className="newsletter__subtitle">
                            Practical writing on web development, backends, and shipping real
                            software plus new posts from the blog. No fluff, unsubscribe anytime.
                        </p>

                        <ul className="newsletter__benefits">
                            {benefits.map((b, i) => (
                                <li key={i} className="benefit__item">
                                    <span className="benefit__icon">
                                        <i className={`bx ${b.icon}`}></i>
                                    </span>
                                    <div>
                                        <h4>{b.title}</h4>
                                        <p>{b.text}</p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Right — the action */}
                    <div className="newsletter__card">
                        {!subscribed ? (
                            <form className="newsletter__form" onSubmit={handleSubscribe}>
                                <h3 className="newsletter__card-title">What are you into?</h3>

                                <div className="topics__grid">
                                    {topics.map(topic => (
                                        <button
                                            type="button"
                                            key={topic.id}
                                            className={`topic__item ${selectedTopics.includes(topic.id) ? 'active' : ''}`}
                                            onClick={() => toggleTopic(topic.id)}
                                            aria-pressed={selectedTopics.includes(topic.id)}
                                        >
                                            <i className={`bx ${topic.icon}`}></i>
                                            <span>{topic.name}</span>
                                        </button>
                                    ))}
                                </div>

                                <div className="newsletter__field">
                                    <i className="bx bx-envelope"></i>
                                    <input
                                        type="email"
                                        placeholder="you@example.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        disabled={loading}
                                    />
                                </div>

                                <button type="submit" className="newsletter__button" disabled={loading}>
                                    {loading ? (
                                        <>
                                            <span className="loading-spinner"></span>
                                            Subscribing...
                                        </>
                                    ) : (
                                        <>
                                            Subscribe <i className="uil uil-message"></i>
                                        </>
                                    )}
                                </button>

                                {error && <p className="newsletter__error">{error}</p>}

                                <p className="newsletter__privacy">
                                    <i className="bx bx-lock-alt"></i>
                                    I respect your privacy. Unsubscribe anytime.
                                </p>
                            </form>
                        ) : (
                            <div className="newsletter__success">
                                <i className="bx bx-check-circle"></i>
                                <h3>You&rsquo;re in! &#127881;</h3>
                                <p>Check your inbox to confirm your first post is on the way.</p>
                                <a
                                    href={BLOG_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="newsletter__success-btn"
                                >
                                    Read the blog <i className="bx bx-right-arrow-alt"></i>
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Newsletter;